import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text, Localizer, withText } from 'preact-i18n';
import Markdown from 'preact-markdown';
import { route } from 'preact-router';
import linkstate from 'linkstate';
import md5 from 'crypto-js/md5';

import fields from '../../lib/fields';

import FormFields from '../../components/form/fields';
import ImageScroller from '../../components/imagescroller';
import HelpText from '../../components/gui/helpText';
import Input from '../../components/form/input';
import GuiList from '../../components/gui/list';
import UserProfileInline from '../../components/user/profileInline';
import Camera from '../../components/gui/camera';

import ConventionViewInfo from './view-info';
import ConventionViewSponsors from './view-sponsors';
import ConventionViewResults from './view-results';
import ConventionViewStages from './view-stages';
import ConventionViewSignup from './view-signup';
import ConventionViewStageAdmin from './view-stage-admin';

import ShotTrainerFieldRemote from '../shottrainer/fieldRemote';
import ShotTrainerFieldConsumer from '../shottrainer/fieldConsumer';

import FirebaseBroadcaster from '../../firebaseBroadcaster';

import localUtil from '../../lib/util';
import { QRCodeSVG } from 'qrcode.react';

const MARKDOWN_OPTIONS = {
	pedantic: false,
	gfm: true,
	breaks: true,
	sanitize: false,
	smartLists: true,
	smartypants: true,
	xhtml: true,
    highlight: function(code, lang) {
        const hljs = require('highlight.js');
        const language = hljs.getLanguage(lang) ? lang : 'plaintext';
        return hljs.highlight(code, { language }).value;
      },
    langPrefix: 'hljs language-', // highlight.js css expects a top-level 'hljs' class.
};

function participantExists(participants, key, value) {
    return participants.some(participant => participant[key] === value);
}

function memberExists(members, key, value) {
    return members.some(member => member[key] === value);
}

function generateArray(X) {
    const myNewShinyArray = [...Array(parseInt(X, 10) + 1).keys()];
    myNewShinyArray.shift();
    return myNewShinyArray;
}


function displayImage(img, props, size = '150x') {
    if (typeof img !== 'object') {
        return undefined;
    }
    const imgSrc = `${img.s3SmallLink}`;
    return imgSrc;
}


@withText(props => ({
    searchTitle: <Text id='input.search-for-user'>Search for user</Text>,
    otpTitle: <Text id='input.otp-input'>Enter OTP</Text>,
}))
@observer
class ConventionView extends Component {
  	constructor(props) {
        super(props);
        this.state = {
            sessionid: new Date().getTime(),
            sections: fields.get('convention'),
            editEnabled: true,
        };
        this.mainContainer = null;
        this.countdownTimer = null;
        this.updateContentTimer = null;
        this.updateTimer = {};
        this.saveTimer = {};
        this.refs = {};

        this.firebaseBroadcaster = new FirebaseBroadcaster();
    }

    resetSummary = () => {
        const { conventionStore } = this.props.stores;
        // Reset the summary object
        conventionStore.updateKeyValue('summary', {});
    }

    loadAll = async (props = this.props, emptyFirst = false) => {
        const { id } = props;
        const { conventionStore, userStore } = this.props.stores;
        conventionStore.deleteReloadTimer();
        const { user } = userStore;
        if (emptyFirst) {
            conventionStore.updateKeyValue('convention', {});
        }
        conventionStore.setRefreshPage(() => {
            this.loadAll();
        });

        await conventionStore.loadClassMap();
        await conventionStore.loadDisciplineMap();

        await conventionStore.load(id, false, {
            query: {
                hostname: conventionStore.currentHostname,
            },
            addData: ['tags', 'participants'],
        });
        const { convention } = conventionStore;
        await conventionStore.getJwt(convention.uuidv4);
        await this.loadResults();

        conventionStore.updateKeyValue('newConvention', {
            title: convention.title,
            body: convention.body,
            images: convention.images,
            stageStands: convention.stageStands,
            stageStandImages: convention.stageStandImages,
        });

        if (convention && convention.participants && convention.participants.length && participantExists(convention.participants, 'id', user.id)) {
            this.setState({ signedUp: true });
        }
        if (convention && convention.members && convention.members.length && memberExists(convention.members, 'id', user.id)) {
            this.setState({ isConventionAdmin: true });
        } else if (convention.user === user.id) {
            this.setState({ isConventionAdmin: true });
        }

        // Count participant stages for each participant and add this to an object with participant id as key.
        // I only need the total number of stages for each participant.

        const participantStages = {};
        if (convention && convention.stages && convention.stages.length) {
            convention.stages.forEach((stage, idx) => {
                const participants = convention[`participants${idx + 1}`];
                if (participants && participants.length) {
                    participants.forEach(participant => {
                        if (!participantStages[participant.id]) {
                            participantStages[participant.id] = 0;
                        }
                        participantStages[participant.id] += 1;
                    });
                }
            });
        }
        this.setState({
            participantStages,
        });

        const from = new Date(convention.startDate);
        const to = new Date(convention.endDate);
        const registrationFrom = new Date(convention.registrationStartDate || convention.startDate);
        registrationFrom.setHours(registrationFrom.getHours() - 1);
        const now = new Date();

        if (from < now && to > now) {
            this.setState({
                isValidDate: true,
                editEnabled: true,
                isRegistrationOpen: registrationFrom < now && to > now ? true : false,
                isStarted: true,
                registrationFrom,
            });
        } else if (from > now) {
            this.setState({
                isInTheFuture: true,
                isValidDate: false,
                isRegistrationOpen: registrationFrom < now && to > now ? true : false,
                registrationFrom,
            });
            const { seconds } = util.dateDiff(now, from);
            this.setState({
                countdown: {
                    seconds,
                },
            });
            clearInterval(this.countdownTimer); // Clear any previous countdown
            this.countdownTimer = setInterval(() => {
                // Update the count down every 1 second
                const { seconds } = util.dateDiff(new Date(), convention.startDate);
                this.setState({
                    countdown: {
                        seconds,
                    },
                });
            }, 1000);
        } else {
            const { days } = util.dateDiff(now, from);
            this.setState({
                isInThePast: true,
                isValidDate: false,
                age: days,
                editEnabled: days < -7 ? false : true,
                registrationFrom,
            });
        }

        if (convention && convention.resultsLocked) {
            this.setState({
                editEnabled: !convention.resultsLocked,
            });
        }

        conventionStore.setReloadTimer(setTimeout(() => {
            this.loadAll();
        }, 60000));
    }

    editConvention = e => {
        const { id, title } = e.target.closest('button').dataset;
        const { drawerLevel = 1 } = this.props;
		const { appState, conventionStore } = this.props.stores;
        conventionStore.deleteReloadTimer();
		const { drawerHeightLarge } = appState;
		appState.openDrawer('editConvention', {
            id,
			height: drawerHeightLarge,
            callback: () => {
                this.loadAll();
            },
		}, drawerLevel + 2);
	}

    addTag = e => {
        const { tagInput = '' } = this.state;
        const { conventionStore } = this.props.stores;
        const value = {
            name: tagInput.toLowerCase().trim(),
            date: new Date(),
        };
        value.md5 = md5(JSON.stringify(value)).toString();

        const field = 'tags';
        const object = conventionStore.convention;
        const tagExists = object[field] && object[field].find(tag => tag.name === value.name);
        if (!tagExists) {
            const localValue = object[field] ? [...object[field]] : [];
            conventionStore.saveField(object.id, field, value);
            conventionStore.updateField(object.id, field, [...localValue, value]);
        }
        this.setState({ tagInput: '' });
    }

    checkForEnter = (e) => {
        if (e.key === 'Enter') {
            this.addTag();
        }
    }

    isFieldShooting = (type) => {
        const isFieldShooting = ['norgesfelt', 'felt', 't-96'].includes(type);
        return isFieldShooting;
    }

    filterParticipants = participant => {
        const { isConventionAdmin, showAll } = this.state;
        if (showAll) {
            return true;
        }
        const { userStore } = this.props.stores;
        const { user } = userStore;
        return user.id === participant.id;
    }

    toggleShowAll = () => {
        const { showAll } = this.state;
        this.setState({ showAll: !showAll });
    }

    updateResult = e => {
        // const { isConventionAdmin } = this.state;
        // if (!isConventionAdmin) {
        //     return;
        // }
        const { value, dataset, name, min, max } = e.target;

        let inputValue = parseInt(value, 10) || 0;

        // console.log({ min, max });
        const realMin = min ? parseInt(min, 10) : 0;
        const realMax = max ? parseInt(max, 10) : 50;
        if (inputValue < realMin) {
            inputValue = realMin;
        }
        if (inputValue > realMax) {
            inputValue = realMax;
        }

        // data-participant={participant.id}
        // data-id={targetResult?.id}
        // data-stage={stage.md5}
        // data-stand={stand}
        const { participant, idx, id, stage, stand, delay = 500, type } = dataset;

        // Check for validity
        if (type === 'norgesfelt') {
            // console.log('updateResult-norgesfelt', {
            //     stage,
            //     stand,
            //     name,
            //     inputValue,
            // });

            const currentStand = parseInt(stand, 10);

            const { conventionStore, conventionResultStore } = this.props.stores;
            const norgesFeltTargetMaxValues = conventionResultStore.getNorgesfeltTargetMaxValues();
            const currentMaxValues = norgesFeltTargetMaxValues.find(item => item.stand === currentStand);

            if (name === 'scoreHits' && inputValue > currentMaxValues.scoreHits) {
                inputValue = currentMaxValues.scoreHits;
            }
            if (name === 'scoreTargets' && inputValue > currentMaxValues.scoreTargets) {
                inputValue = currentMaxValues.scoreTargets;
            }
            if (name === 'scoreInner' && inputValue > currentMaxValues.scoreInner) {
                inputValue = currentMaxValues.scoreInner;
            }
        } else if (type === 'felt') {
            // console.log('updateResult-felt', {
            //     stage,
            //     stand,
            //     name,
            //     inputValue,
            // });
            const { conventionStore } = this.props.stores;
            const { convention } = conventionStore;
            const { stageStands = [] } = convention;
            const currentStand = parseInt(stand, 10);
            const currentMaxValues = stageStands.find(item => parseInt(item.stand, 10) === currentStand);
            // console.log('currentMaxValues', currentMaxValues);
            const maxHits = parseInt(currentMaxValues?.scoreHits, 10) || 6;
            const maxTargets = parseInt(currentMaxValues?.scoreTargets, 10) || 6;
            const maxInner = parseInt(currentMaxValues?.scoreInner, 10) || 6;
            if (name === 'scoreHits' && inputValue > maxHits) {
                inputValue = maxHits;
            }
            if (name === 'scoreTargets' && inputValue > maxTargets) {
                inputValue = maxTargets;
            }
            if (name === 'scoreInner' && inputValue > maxInner) {
                inputValue = maxInner;
            }
        }

        const delayValue = parseInt(delay, 10) || 500;
        // console.log('updateResult', participant, id, idx, value);
        clearTimeout(this.saveTimer[`${participant}-${stage}-${stand}-${name}`]);
        const timerId = `${participant}-${stage}-${stand}-${name}`;
        clearTimeout(this.updateTimer[timerId]);
        this.updateTimer[timerId] = setTimeout(() => {
            this.doUpdateResult(e, inputValue);
        }, parseInt(delayValue, 10));
    }

    doUpdateResult = async (e, value) => {
        const { dataset, name = 'scoreTotal' } = e.target;
        const { participant, idx, id, stage, stand } = dataset;
        // console.log('doUpdateResult', participant, id, idx, value);
        const { conventionStore, conventionResultStore } = this.props.stores;
        const { convention } = conventionStore;
        clearTimeout(this.saveTimer[`${participant}-${stage}-${stand}-${name}`]);

        if (id) {
            try {
                await this.firebaseBroadcaster.sendCommand('result', {
                    id: parseInt(id, 10),
                    targetIndex: parseInt(idx, 10),
                    participant: parseInt(participant, 10),
                    convention: convention.id,
                    stage,
                    stand,
                    [name]: parseInt(value, 10),
                });
                // this.firebaseBroadcaster.verifyCommands();
                // this.firebaseBroadcaster.verifyServerCommands();
            } catch (error) {
                console.error('Error sending command to Firebase:', error);
            }
            await conventionResultStore.save({
                id: parseInt(id, 10),
                targetIndex: parseInt(idx, 10),
                participant: parseInt(participant, 10),
                convention: convention.id,
                stage,
                stand,
                [name]: parseInt(value, 10),
            });
        } else {
            try {
                this.firebaseBroadcaster.sendCommand('result', {
                    targetIndex: parseInt(idx, 10),
                    participant: parseInt(participant, 10),
                    convention: convention.id,
                    stage,
                    stand,
                    [name]: parseInt(value, 10),
                });
            } catch (error) {
                console.error('Error sending command to Firebase:', error);
            }
            await conventionResultStore.insert({
                targetIndex: parseInt(idx, 10),
                participant: parseInt(participant, 10),
                convention: convention.id,
                stage,
                stand,
                [name]: parseInt(value, 10),
            });
        }

        this.saveTimer[`${participant}-${stage}-${stand}-${name}`] = setTimeout(() => {
            this.loadResults();
        }, 100);

        let nextStand = parseInt(stand, 10);
        let nextField = 'scoreTotal';
        switch (name) {
            case 'scoreTotal':
                nextField = 'scoreHits';
                break;
            case 'scoreHits':
                nextField = 'scoreTargets';
                break;
            case 'scoreTargets':
                nextField = 'scoreInner';
                break;
            case 'scoreInner':
                nextField = 'scoreHits';
                nextStand += 1;
                console.log('nextStand', nextStand);
                // nextField = 'scorePenalty';
                break;
        }

        const refKey = `${participant}-${stage}-${nextStand}-${nextField}`;
        // console.log('refKey', refKey);
        if (this.refs[refKey]) {
            this.refs[refKey].focus();
        }
    }

    loadResults = async () => {
        const { conventionStore, conventionResultStore } = this.props.stores;
        const { convention } = conventionStore;
        await conventionResultStore.load({ query: { convention: convention.id } });
    }

    addImage = e => {
        const { id } = e.target.dataset;
        const { drawerLevel = 1 } = this.props;
        const { appState } = this.props.stores;
        const { drawerHeightSmall } = appState;
        appState.openDrawer('editConventionResultImage', {
            id,
            height: drawerHeightSmall,
        }, drawerLevel + 1);
    }

    viewImage = e => {
        const { id } = e.target.closest('td').dataset;
        const { drawerLevel = 1 } = this.props;
        const { appState } = this.props.stores;
        const { drawerHeightMedium } = appState;
        appState.openDrawer('viewConventionResultImage', {
            id,
            height: drawerHeightMedium,
        }, drawerLevel + 1);
    }

    viewEditImage = e => {
        const { id } = e.target.closest('i').dataset;
        const { drawerLevel = 1 } = this.props;
        const { appState } = this.props.stores;
        const { drawerHeightMedium } = appState;
        appState.openDrawer('viewConventionResultImage', {
            id,
            height: drawerHeightMedium,
        }, drawerLevel + 1);
    }

    handleFocus = (e) => {
        e.target.select();
    }

    // handleBlur = async (e) => {
    //     await this.loadResults();
    // }
    // handleContextMenu = (e) => {
    //     return ;
    //     e.preventDefault();
    // }

    editParticipant = async e => {
        const { id, md5, stage, idx } = e.target.closest('i, button').dataset;
        const { drawerLevel = 1 } = this.props;
        const { appState } = this.props.stores;
        const { drawerHeightSmall } = appState;
        appState.openDrawer('editConventionParticipant', {
            id,
            md5,
            stage,
            idx,
            height: drawerHeightSmall,
        }, drawerLevel + 1);
    }

    addParticipant = async e => {
        const { id, md5 } = e.target.closest('button').dataset;
        const { conventionStore } = this.props.stores;
        const { convention } = conventionStore;
        const { participants } = convention;
        const user = participants.find(user => user.id === parseInt(id, 10));
        await conventionStore.newParticipant(user, md5);
        await this.loadAll();
    }

    addMe = async e => {
        const { md5 } = e.target.closest('button').dataset;
        const { conventionStore, userStore } = this.props.stores;
        const { user } = userStore;
        await conventionStore.newParticipant(user, md5);
        await this.loadAll();
    }

    editStage = async e => {
        const { id, md5, idx } = e.target.closest('button').dataset;
        const { drawerLevel = 1 } = this.props;
        const { appState } = this.props.stores;
        const { drawerHeightMedium } = appState;
        appState.openDrawer('editConventionStage', {
            id,
            idx,
            md5,
            height: drawerHeightMedium,
        }, drawerLevel + 1);
    }

    componentDidMount() {
        this.loadAll(this.props, true);
        this.resetSummary();
    }

    componentWillReceiveProps(nextProps) {
        if (nextProps.page !== this.props.page || nextProps.artid !== this.props.artid) {
            this.loadAll(nextProps, true);
            this.resetSummary();
        }
    }

    componentWillUnmount() {
        clearInterval(this.countdownTimer);
        const { conventionStore } = this.props.stores;
        conventionStore.deleteReloadTimer();
    }

    render() {
        const {
            searchTitle,
            otpTitle,
            id,
        } = this.props;
        const {
            search,
            userOtp,
            userOtpIsValid,
            userOtpError,
            participantStages = {},
        } = this.state;

        const {
            signedUp,
            isConventionAdmin,
            isValidDate,
            isInTheFuture,
            isInThePast,
            isRegistrationOpen,
            isStarted,
            registrationFrom,
            countdown = {},
            age,
            editEnabled,
            showAll,
        } = this.state;
        const { appState, userStore, conventionStore, conventionResultStore } = this.props.stores;
        const { mainView, subView, isDevelopment, path } = appState;
        const { user = {}, isAdmin, isCustomerAdmin, isTester, isVeterinary, language, searchUsersResult } = userStore;
        const darkmode= util.getNestedValue(user, 'settings.darkmode');

        const { convention, jwtToken, otp = '' } = conventionStore;
        const { stageStands = [] } = convention;
        const norgesFeltTargetMaxValues = conventionResultStore.getNorgesfeltTargetMaxValues();

        const { conventionResults } = conventionResultStore;

        const apiServer = util.getApiServer();

        const otpFirstPart = otp.slice(0, 3);
        const otpSecondPart = otp.slice(3, 6);

        return (<>
            <div class='w-100 d-flex justify-content-center'>
                <div class='w-100 h-100' style='max-width: 960px; padding-top: 20px; padding-bottom: 100px;'>

                    <div class={`font-weight-lighter px-3 mt-0 box-header w-100 position-relative`}>
                        {convention.title}
                        {isCustomerAdmin && <div class={`position-absolute`} style={`top: 0px; right: 10px; z-index: 1000;`}>
                            <button
                                onClick={this.editConvention}
                                class={`btn btn-sm btn-outline-primary rounded-pill mr-2`}
                                data-id={convention.id}
                            >
                                <i class={`fa-regular fa-pen`} />
                            </button>
                        </div>}
                    </div>
                    <div class='w-100 d-flex flex-column'>
                        <div class='px-0 box-header-info d-flex flex-column'>

                            <ConventionViewInfo
                                stores={this.props.stores}
                                isStarted={isStarted}
                            />

                            <ConventionViewSponsors
                                stores={this.props.stores}
                            />

                            <ConventionViewSignup
                                stores={this.props.stores}
                                isInTheFuture={isInTheFuture}
                                isRegistrationOpen={isRegistrationOpen}
                                signedUp={signedUp}
                                isConventionAdmin={isConventionAdmin}
                                countdown={countdown}
                            />

                            {(isInThePast && convention.id && conventionResults.length > 0) ? <>
                                <ConventionViewResults
                                    conventionId={convention.id}
                                    stores={this.props.stores}
                                    isConventionAdmin={isConventionAdmin}
                                    conventionResultsLength={conventionResults?.length}
                                />
                            </> : <>
                                <div class='font-weight-lighter px-3 box-header d-flex flex-row justify-content-between mb-1 mt-3'>
                                    <Text id='convention.participants'>Participants</Text>
                                </div>
                                <div class={`d-flex flex-wrap mt-0 mb-3 mx-3 px-3 py-3 rounded-lg ${darkmode ? 'bg-darkmode' : 'bg-lightmode'} overflow-auto`}>
                                    {convention.participants && convention.participants.map(participant => {
                                        const isYou = participant.id === user.id;
                                        return(<>
                                            <span class='badge badge-info font-weight-normal rounded-pill mx-1 mb-1'>
                                                {isConventionAdmin && participant.signupDate ? <>
                                                    <span class='font-weight-lighter mr-1'>{util.isoTime(participant.signupDate)}:</span>
                                                </> : <></>}
                                                {localUtil.displayName(participant)}
                                                {/* {isYou ? `(You)` : ''} */}
                                                <span class='font-weight-lighter ml-1'>({participantStages[participant.id] || 0})</span>
                                            </span>
                                        </>);
                                    })}
                                </div>
                            </>}


                            {convention.isAdminScoring && <>
                                <div class='font-weight-lighter px-3 box-header d-flex flex-row justify-content-between mb-1 mt-1'>
                                    <Text id='convention.info'>Info</Text>
                                </div>
                                <div class={`d-flex flex-wrap mt-0 mb-3 mx-3 px-3 py-3 rounded-lg ${darkmode ? 'bg-darkmode' : 'bg-lightmode'} overflow-auto`}>
                                    <div class='d-flex w-100 bg-danger text-white rounded-lg p-3 mb-3'>
                                        <span class='mr-3' style='font-size: 2.0em;'><i class="fa-regular fa-circle-info" /></span>
                                        <Text id='convention.isapproved-info'>This convention is approved by NIF. Adding/editing score is only possible by approved range officials.</Text>
                                    </div>
                                </div>
                            </>}

                            {convention.stages && convention.stages.length ? <>
                                {convention.stages.map((stage, idx) => {
                                    const isAllowedToEdit = (editEnabled && !parseInt(stage.stageClosed, 10) && !parseInt(stage.stageDone, 10));
                                    const isFieldShooting = this.isFieldShooting(stage.type);
                                    const participants = convention[`participants${idx + 1}`];
                                    const isSignedUpToStage = participants && participants.some(participant => participant.id === user.id);
                                    const stageDone = parseInt(stage.stageDone, 10);
                                    const stageClosed = parseInt(stage.stageClosed, 10);

                                    return (<>
                                        <div class='font-weight-lighter px-3 box-header d-flex flex-row justify-content-between mb-1 mt-3'>
                                            {stage.name} ({stage.type})
                                            <div class='d-flex align-items-center justify-content-center'>
                                                {signedUp && !isSignedUpToStage && isRegistrationOpen && !convention.isApproved && !stageDone ? <>
                                                    <button
                                                        class='btn btn-sm btn-primary rounded-pill'
                                                        data-md5={stage.md5}
                                                        onClick={this.addMe}
                                                    >
                                                        <i class='fa-duotone fa-user-plus' /> <Text id='convention.add-me'>Add me</Text>
                                                    </button>
                                                </> : <></>}
                                                <button class={`btn btn-sm btn-${showAll ? 'primary' : 'outline-secondary'} rounded-pill ml-3`} onClick={this.toggleShowAll}>
                                                    <i class='fa-duotone fa-users' />
                                                </button>
                                                {isConventionAdmin && <>
                                                    <button
                                                        class='btn btn-sm btn-outline-primary rounded-pill ml-3'
                                                        data-id={convention.id}
                                                        data-md5={stage.md5}
                                                        data-idx={idx}
                                                        onClick={this.editStage}
                                                    >
                                                        <i class='fa-duotone fa-edit' />
                                                    </button>
                                                </>}
                                            </div>
                                        </div>
                                        <div class={`d-flex flex-column mt-0 mb-3 mx-3 px-3 py-3 rounded-lg ${darkmode ? 'bg-darkmode' : 'bg-lightmode'} overflow-auto`}>
                                            <div class='d-flex flex-row justify-content-start align-items-center mb-1'>
                                                {isInThePast || stageDone ? <>
                                                    <i class='fa-solid fa-check mr-1 text-success' />
                                                </> : <>
                                                    <i class='fa-regular fa-clock mr-1 text-muted' />
                                                </>}
                                                {util.isoTime(stage.date)}
                                                <i class='fa-regular fa-person ml-3 mr-1 text-muted' /> {participants?.length}/{stage?.stageSlots}

                                                {isInThePast || stageDone ? <>
                                                    <span class='badge badge-success font-weight-normal rounded-pill ml-3'>
                                                        <Text id='convention.stage-done'>Stage done</Text>
                                                    </span>
                                                </> : <>
                                                    {stageClosed ? <>
                                                        <span class='badge badge-warning font-weight-normal rounded-pill ml-3'>
                                                            <Text id='convention.stage-upcoming'>Stage not started</Text>
                                                        </span>
                                                    </> : <>
                                                        <span class='badge badge-danger font-weight-normal rounded-pill ml-3'>
                                                            <Text id='convention.stage-running'>Stage running</Text>
                                                        </span>
                                                    </>}
                                                </>}
                                            </div>
                                            <div class='table-reponsive w-100'>
                                                <table class='table table-sm table-striped'>
                                                    <thead>
                                                        <tr>
                                                            <th class='text-muted w-5'><small>&nbsp;</small></th>

                                                            {isFieldShooting && !isAllowedToEdit ? <>
                                                                {generateArray(stage.stages || 10).map(stand => {
                                                                    return (<>
                                                                        <th class='text-muted text-center'><small>{stand}</small></th>
                                                                    </>);
                                                                })}
                                                            </> : <>
                                                                {this.isFieldShooting(stage.type) ? <>
                                                                    <th class='text-muted text-center w-5'><small><i class='fa-solid fa-crosshairs-simple' /></small></th>
                                                                    <th class='text-muted text-center w-5'><small><i class='fa-solid fa-image' /></small></th>
                                                                </> : <></>}
                                                                {conventionResultStore.getParticipantFields(stage).map((field, index) => {
                                                                    return (<>
                                                                        <th class='text-muted font-weight-lighter w-5'><small>{field}</small></th>
                                                                    </>);
                                                                })}
                                                            </>}

                                                            {this.isFieldShooting(stage.type) ? <>
                                                                <th class='text-muted text-center w-5'><small><Text id='input.innerX'>iX</Text></small></th>
                                                            </> : <></>}
                                                            <th class='text-muted text-center w-5'><small><Text id='input.totalScore'>Total</Text></small></th>
                                                            <th class='text-muted text-center'><small>&nbsp;</small></th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {participants && participants.filter(this.filterParticipants).map((participant, participantIdx) => {
                                                            const participantResults = conventionResults.filter(result => result.participant === participant.id && result.stage === stage.md5);
                                                            let totalScore = 0;
                                                            let totalIx = 0;
                                                            let standScore = 0;
                                                            const isYou = participant.id === user.id;
                                                            const isAllowedToEditSignup = isAdmin || isYou;
                                                            const isAllowToEditScore = (convention.isAdminScoring ? isConventionAdmin : (isConventionAdmin || isYou) && !stageDone);
                                                            let participantDiffToMax = 0;
                                                            let participantDiffToMaxInner = 0;
                                                            let participantMaxScore = 0;
                                                            let participantMaxInner = 0;

                                                            const participantMaxShots = ['sp.rev', 'sp.pistol', 'mag1', 'mag2'].includes(participant.class) ? 5 : 6;

                                                            if (!isAllowedToEdit && isFieldShooting) {
                                                                return(<>
                                                                    <tr>
                                                                        <td class='p-0 middle'>
                                                                            <nobr>
                                                                                {localUtil.displayName(participant)} <span class='font-weight-lighter'>- {participant.class || 'Missing class'}</span>
                                                                                {isAllowedToEditSignup && !stageDone ? <>
                                                                                    <i
                                                                                        class='fa-solid fa-edit ml-2'
                                                                                        data-id={convention.id}
                                                                                        data-md5={participant.md5}
                                                                                        data-stage={stage.md5}
                                                                                        data-idx={idx}
                                                                                        onClick={this.editParticipant}
                                                                                    />
                                                                                </> : <></>}
                                                                            </nobr>
                                                                        </td>
                                                                        {generateArray(stage.stages || 10).map(stand => {
                                                                            standScore = 0;
                                                                            const targetResult = participantResults.find(result => result.stand === stand);
                                                                            standScore += targetResult?.scoreHits || 0;
                                                                            standScore += targetResult?.scoreTargets || 0;
                                                                            if (targetResult?.scorePenalty) {
                                                                                standScore -= targetResult.scorePenalty;
                                                                            }
                                                                            totalScore += standScore;

                                                                            const standInner = targetResult?.scoreInner || 0;
                                                                            totalIx += standInner;

                                                                            const currentStand = parseInt(stand, 10);
                                                                            let currentMaxValues = {};
                                                                            if (stage.type === 'norgesfelt') {
                                                                                currentMaxValues = norgesFeltTargetMaxValues.find(item => item.stand === currentStand);
                                                                            } else if (stage.type === 'felt') {
                                                                                currentMaxValues = stageStands.find(item => parseInt(item.stand, 10) === currentStand);
                                                                            }
                                                                            let maxHits = parseInt(currentMaxValues?.scoreHits, 10) || 6;
                                                                            let maxTargets = parseInt(currentMaxValues?.scoreTargets, 10) || 6;
                                                                            const maxGroups = parseInt(currentMaxValues?.scoreGroups, 10) || 0;
                                                                            let maxInner = parseInt(currentMaxValues?.scoreInner, 10) || 6;
                                                                            if (participantMaxShots < 6) {
                                                                                maxTargets = maxTargets > participantMaxShots ? participantMaxShots : maxTargets;
                                                                                maxInner = participantMaxShots;
                                                                                maxHits = participantMaxShots;
                                                                            }
                                                                            let maxScore = parseInt(maxHits + maxTargets, 10) || 0;

                                                                            const diffToMax = standScore - maxScore;
                                                                            const diffToMaxInner = standInner - maxInner;

                                                                            participantDiffToMax += diffToMax;
                                                                            participantDiffToMaxInner += diffToMaxInner;
                                                                            participantMaxScore += maxScore;
                                                                            participantMaxInner += maxInner;

                                                                            return (<>
                                                                                <td class='text-center' data-id={targetResult?.id} onClick={this.viewImage}>
                                                                                    <span class={`${standScore > maxScore ? 'text-danger' : ''}`}>{standScore}</span>/
                                                                                    <span class={`${standInner > maxInner ? 'text-danger' : ''} font-weight-lighter`}>{standInner}</span>
                                                                                </td>
                                                                            </>);
                                                                        })}
                                                                        <td class='text-center font-weight-lighter position-relative'>
                                                                            {totalIx}
                                                                            <div class='position-absolute' style={`bottom: 0px; right: 0px; font-size: 0.5em;`}>
                                                                                <span class='font-weight-lighter'>
                                                                                    {participantMaxInner}
                                                                                </span>
                                                                            </div>
                                                                        </td>
                                                                        <td class='text-center font-weight-bolder position-relative'>
                                                                            {totalScore}
                                                                            <div class='position-absolute' style={`bottom: 0px; right: 0px; font-size: 0.5em;`}>
                                                                                <span class='font-weight-lighter'>
                                                                                    {participantMaxScore}
                                                                                </span>
                                                                            </div>
                                                                        </td>
                                                                    </tr>
                                                                </>);
                                                            }

                                                            if (isFieldShooting) {
                                                                const totalStands = stage.stages || 10;
                                                                return (<>
                                                                    {participantIdx > 0 && <>
                                                                        <tr>
                                                                            <th class='text-muted'><small>&nbsp;</small></th>
                                                                            {isFieldShooting && !isAllowedToEdit ? <>
                                                                                {generateArray(stage.stages || 10).map(stand => {
                                                                                    return (<>
                                                                                        <th class='text-muted text-center w-5'><small>{stand}</small></th>
                                                                                    </>);
                                                                                })}
                                                                            </> : <>
                                                                                {this.isFieldShooting(stage.type) ? <>
                                                                                    <th class='text-muted text-center'><small><i class='fa-solid fa-crosshairs-simple' /></small></th>
                                                                                    <th class='text-muted text-center w-5'><small><i class='fa-solid fa-image' /></small></th>
                                                                                </> : <></>}
                                                                                {conventionResultStore.getParticipantFields(stage).map((field, index) => {
                                                                                    return (<>
                                                                                        <th class='text-muted font-weight-lighter w-5'><small>{field}</small></th>
                                                                                    </>);
                                                                                })}
                                                                            </>}
                                                                            {this.isFieldShooting(stage.type) ? <>
                                                                                <th class='text-muted text-center w-5'><small><Text id='input.innerX'>iX</Text></small></th>
                                                                            </> : <></>}
                                                                            <th class='text-muted text-center w-5'><small><Text id='input.totalScore'>Total</Text></small></th>
                                                                            <th class='text-muted text-center'><small>&nbsp;</small></th>
                                                                        </tr>
                                                                    </>}

                                                                    {(!participant.class && isAllowedToEditSignup) ? <>
                                                                        <tr>
                                                                            <td class='p-0 middle' colspan='10'>

                                                                                <div class='bg-danger text-white rounded-lg p-3 my-3'>
                                                                                    <span class='mr-3' style='font-size: 2.0em;'><i class="fa-solid fa-hand" /></span>
                                                                                    <span style='font-size: 1.3em;'><Text id='convention.no-class-info'>No class selected!</Text></span>
                                                                                    <button
                                                                                        class='btn btn-primary btn-block rounded-pill mt-2'
                                                                                        data-id={convention.id}
                                                                                        data-md5={participant.md5}
                                                                                        data-stage={stage.md5}
                                                                                        data-idx={idx}
                                                                                        onClick={this.editParticipant}
                                                                                    >
                                                                                        <i class='fa-solid fa-pen' /> <Text id='convention.add-shooting-class'>Add shooting class</Text>
                                                                                    </button>
                                                                                </div>

                                                                            </td>
                                                                        </tr>
                                                                    </> : <></>}

                                                                    {generateArray(totalStands).map(stand => {
                                                                        standScore = 0;
                                                                        const targetResult = participantResults.find(result => result.stand === stand);
                                                                        const standHits = targetResult?.scoreHits || 0;
                                                                        const standTargets = targetResult?.scoreTargets || 0;
                                                                        standScore += standHits;
                                                                        standScore += targetResult?.scoreTargets || 0;
                                                                        if (targetResult?.scorePenalty) {
                                                                            standScore -= targetResult.scorePenalty;
                                                                        }
                                                                        totalScore += standScore;

                                                                        const standInner = targetResult?.scoreInner || 0;
                                                                        totalIx += standInner;

                                                                        const standIdx = parseInt(stand, 10) - 1;
                                                                        const standImage = convention.stageStandImages ? convention.stageStandImages[standIdx] : null;

                                                                        const currentStand = parseInt(stand, 10);
                                                                        let currentMaxValues = {};
                                                                        if (stage.type === 'norgesfelt') {
                                                                            currentMaxValues = norgesFeltTargetMaxValues.find(item => item.stand === currentStand);
                                                                        } else if (stage.type === 'felt') {
                                                                            currentMaxValues = stageStands.find(item => parseInt(item.stand, 10) === currentStand);
                                                                        }
                                                                        let maxHits = parseInt(currentMaxValues?.scoreHits, 10) || 6;
                                                                        let maxTargets = parseInt(currentMaxValues?.scoreTargets, 10) || 6;
                                                                        const maxGroups = parseInt(currentMaxValues?.scoreGroups, 10) || 0;
                                                                        let maxInner = parseInt(currentMaxValues?.scoreInner, 10) || 6;
                                                                        if (participantMaxShots < 6) {
                                                                            maxTargets = maxTargets > participantMaxShots ? participantMaxShots : maxTargets;
                                                                            maxInner = participantMaxShots;
                                                                            maxHits = participantMaxShots;
                                                                        }
                                                                        const maxScore = parseInt(maxHits + maxTargets, 10) || 0;


                                                                        const diffToMax = standScore - maxScore;
                                                                        const diffToMaxInner = standInner - maxInner;

                                                                        participantDiffToMax += diffToMax;
                                                                        participantDiffToMaxInner += diffToMaxInner;
                                                                        participantMaxScore += maxScore;
                                                                        participantMaxInner += maxInner;

                                                                        return (<>
                                                                            <tr>
                                                                                {isAllowedToEdit ? <>
                                                                                    {stand === 1 && <>
                                                                                        <td
                                                                                            class={`middle p-0`}
                                                                                            rowspan={totalStands}
                                                                                            style={'background-color: rgba(0,0,255,0.1);'}
                                                                                        >
                                                                                            <div class='vertical-text'>
                                                                                                <nobr>
                                                                                                    {localUtil.displayName(participant)} <span class='font-weight-lighter'>- {participant.class}</span>
                                                                                                    {isAllowedToEditSignup ? <>
                                                                                                        <i
                                                                                                            class='fa-solid fa-edit mt-2'
                                                                                                            data-id={convention.id}
                                                                                                            data-md5={participant.md5}
                                                                                                            data-stage={stage.md5}
                                                                                                            data-idx={idx}
                                                                                                            onClick={this.editParticipant}
                                                                                                        />
                                                                                                    </> : <></>}
                                                                                                </nobr>
                                                                                            </div>
                                                                                        </td>
                                                                                    </>}
                                                                                </> : <>
                                                                                    <td class='p-0 middle'>
                                                                                        <nobr>
                                                                                            {localUtil.displayName(participant)} <span class='font-weight-lighter'>- {participant.class}</span>
                                                                                            {isAllowedToEditSignup ? <>
                                                                                                <i
                                                                                                    class='fa-duotone fa-edit ml-2'
                                                                                                    data-id={convention.id}
                                                                                                    data-md5={participant.md5}
                                                                                                    data-stage={stage.md5}
                                                                                                    data-idx={idx}
                                                                                                    onClick={this.editParticipant}
                                                                                                />
                                                                                            </> : <></>}
                                                                                        </nobr>
                                                                                    </td>

                                                                                </>}
                                                                                <td
                                                                                    class='text-center font-weight-lighter middle p-0 position-relative'
                                                                                    style={'background-color: rgba(0,0,255,0.1);'}
                                                                                >
                                                                                    {stand}
                                                                                </td>
                                                                                <td
                                                                                    class='text-center font-weight-lighter middle p-0'
                                                                                    style={'background-color: rgba(0,0,255,0.1);'}
                                                                                >
                                                                                    {standImage && <>
                                                                                        <img src={displayImage(standImage, this.props, '220x')} class='img-fluid' style='max-height: 30px; right: 0px;' />
                                                                                    </>}
                                                                                </td>
                                                                                <td class='text-center p-0'>
                                                                                    {isAllowedToEdit ? <>
                                                                                        <input
                                                                                            type='tel'
                                                                                            pattern='[0-9]*'
                                                                                            inputmode='numeric'
                                                                                            min='0'
                                                                                            max='6'
                                                                                            class={`form-control form-control-sm py-0 px-1 text-center ${standHits > maxHits ? 'bg-danger-light' : ''}`}
                                                                                            style='width: 40px; min-width: 40px; max-width: 40px; font-size: 1.1rem; line-height: 1.2rem;'
                                                                                            size='2'
                                                                                            data-participant={participant.id}
                                                                                            data-id={targetResult?.id}
                                                                                            data-stage={stage.md5}
                                                                                            data-type={stage.type}
                                                                                            data-stand={stand}
                                                                                            data-delay={50}
                                                                                            onInput={this.updateResult}
                                                                                            onFocus={this.handleFocus}
                                                                                            // onContextMenu={this.handleContextMenu}
                                                                                            name='scoreHits'
                                                                                            value={targetResult?.scoreHits >= 0 ? targetResult?.scoreHits : ''}
                                                                                            disabled={isAllowToEditScore ? '' : 'disabled'}
                                                                                            ref={c => this.refs[`${participant.id}-${stage.md5}-${stand}-scoreHits`] = c}
                                                                                            autoComplete='off'
                                                                                        />
                                                                                    </> : <>{targetResult?.scoreHits || ''}</>}
                                                                                </td>
                                                                                <td class='text-center p-0 position-relative'>
                                                                                    {isAllowedToEdit ? <>
                                                                                        <input
                                                                                            type='tel'
                                                                                            pattern='[0-9]*'
                                                                                            inputmode='numeric'
                                                                                            min='0'
                                                                                            max='6'
                                                                                            class={`form-control form-control-sm py-0 px-1 text-center ${(standTargets > maxTargets || standTargets > standHits) ? 'bg-danger-light' : ''}`}
                                                                                            style='width: 40px; min-width: 40px; max-width: 40px; font-size: 1.1rem; line-height: 1.2rem;'
                                                                                            size='2'
                                                                                            data-participant={participant.id}
                                                                                            data-id={targetResult?.id}
                                                                                            data-stage={stage.md5}
                                                                                            data-type={stage.type}
                                                                                            data-stand={stand}
                                                                                            data-delay={50}
                                                                                            onInput={this.updateResult}
                                                                                            onFocus={this.handleFocus}
                                                                                            // onContextMenu={this.handleContextMenu}
                                                                                            name='scoreTargets'
                                                                                            value={targetResult?.scoreTargets >= 0 ? targetResult?.scoreTargets : ''}
                                                                                            disabled={isAllowToEditScore ? '' : 'disabled'}
                                                                                            ref={c => this.refs[`${participant.id}-${stage.md5}-${stand}-scoreTargets`] = c}
                                                                                            autoComplete='off'
                                                                                        />
                                                                                    </> : <>{targetResult?.scoreTargets || ''}</>}
                                                                                    <div class='position-absolute' style={`bottom: 0px; right: 20%; font-size: 0.5em;`}>
                                                                                        <span class='font-weight-lighter'>{maxTargets}</span>
                                                                                    </div>
                                                                                </td>
                                                                                <td class='text-center p-0'>
                                                                                    {isAllowedToEdit ? <>
                                                                                        <input
                                                                                            type='tel'
                                                                                            pattern='[0-9]*'
                                                                                            inputmode='numeric'
                                                                                            min='0'
                                                                                            max='6'
                                                                                            class={`form-control form-control-sm py-0 px-1 text-center ${(standInner > maxInner || standInner > standHits) ? 'bg-danger-light' : ''}`}
                                                                                            style='width: 40px; min-width: 40px; max-width: 40px; font-size: 1.1rem; line-height: 1.2rem;'
                                                                                            size='2'
                                                                                            data-participant={participant.id}
                                                                                            data-id={targetResult?.id}
                                                                                            data-stage={stage.md5}
                                                                                            data-type={stage.type}
                                                                                            data-stand={stand}
                                                                                            data-delay={50}
                                                                                            onInput={this.updateResult}
                                                                                            onFocus={this.handleFocus}
                                                                                            // onContextMenu={this.handleContextMenu}
                                                                                            name='scoreInner'
                                                                                            value={targetResult?.scoreInner >= 0 ? targetResult?.scoreInner : ''}
                                                                                            disabled={isAllowToEditScore ? '' : 'disabled'}
                                                                                            ref={c => this.refs[`${participant.id}-${stage.md5}-${stand}-scoreInner`] = c}
                                                                                            autoComplete='off'
                                                                                        />
                                                                                    </> : <>{targetResult?.scoreInner || ''}</>}
                                                                                </td>
                                                                                <td class='text-center p-0'>
                                                                                    {isAllowedToEdit ? <>
                                                                                        <input
                                                                                            type='tel'
                                                                                            pattern='[0-9]*'
                                                                                            inputmode='numeric'
                                                                                            min='0'
                                                                                            max='6'
                                                                                            class='form-control form-control-sm py-0 px-1 text-danger text-center'
                                                                                            style='width: 40px; min-width: 40px; max-width: 40px; font-size: 1.1rem; line-height: 1.2rem;'
                                                                                            size='2'
                                                                                            data-participant={participant.id}
                                                                                            data-id={targetResult?.id}
                                                                                            data-stage={stage.md5}
                                                                                            data-type={stage.type}
                                                                                            data-stand={stand}
                                                                                            data-delay={50}
                                                                                            onInput={this.updateResult}
                                                                                            onFocus={this.handleFocus}
                                                                                            // onContextMenu={this.handleContextMenu}
                                                                                            name='scorePenalty'
                                                                                            value={targetResult?.scorePenalty >= 0 ? targetResult?.scorePenalty : ''}
                                                                                            disabled={isAllowToEditScore ? '' : 'disabled'}
                                                                                            ref={c => this.refs[`${participant.id}-${stage.md5}-${stand}-scorePenalty`] = c}
                                                                                            autoComplete='off'
                                                                                        />
                                                                                    </> : <>{targetResult?.scorePenalty || ''}</>}
                                                                                </td>
                                                                                <td class={`text-center middle p-0 position-relative ${standInner > maxInner ? 'text-danger' : ''}`}>
                                                                                    {standInner || ''}
                                                                                    <div class='position-absolute' style={`bottom: 0px; right: 0px; font-size: 0.5em;`}>
                                                                                        <span class='font-weight-lighter'>{diffToMaxInner}</span>
                                                                                    </div>
                                                                                </td>
                                                                                <td class={`text-center middle p-0 position-relative ${standScore > maxScore ? 'text-danger' : ''}`}>
                                                                                    {standScore}
                                                                                    <div class='position-absolute' style={`bottom: 0px; right: 0px; font-size: 0.5em;`}>
                                                                                        <span class='font-weight-lighter'>{diffToMax}</span>
                                                                                    </div>
                                                                                </td>
                                                                                <td class='text-center middle p-0'>
                                                                                    {targetResult?.id ? <>
                                                                                        {targetResult?.images?.length ? <>
                                                                                            <i
                                                                                                class={`fa-duotone fa-eye text-muted`}
                                                                                                onClick={this.viewEditImage}
                                                                                                data-id={targetResult?.id}
                                                                                            />
                                                                                        </> : <>
                                                                                            <i
                                                                                                class={`fa-duotone fa-camera text-muted`}
                                                                                                onClick={this.addImage}
                                                                                                data-id={targetResult?.id}
                                                                                            />
                                                                                        </>}
                                                                                    </>: <></>}
                                                                                </td>
                                                                            </tr>
                                                                        </>);
                                                                    })}
                                                                    <tr style='background-color: rgba(255,0,0,0.1); border-bottom: 2px solid #000000;'>
                                                                        <td class='text-right font-weight-bolder' colspan='7'><Text id='input.totalScore'>Total</Text></td>
                                                                        <td class='text-center font-weight-lighter position-relative'>
                                                                            {totalIx}
                                                                            <div class='position-absolute' style={`bottom: 0px; right: 0px; font-size: 0.5em;`}>
                                                                                <span class='font-weight-lighter'>
                                                                                    {participantMaxInner}
                                                                                </span>
                                                                            </div>
                                                                        </td>
                                                                        <td class='text-center font-weight-bolder position-relative'>
                                                                            {totalScore}
                                                                            <div class='position-absolute' style={`bottom: 0px; right: 0px; font-size: 0.5em;`}>
                                                                                <span class='font-weight-lighter'>
                                                                                    {participantMaxScore}
                                                                                </span>
                                                                            </div>
                                                                        </td>
                                                                        <td class='text-center middle p-0'>&nbsp;</td>
                                                                    </tr>
                                                                </>);
                                                            }

                                                            return (<>
                                                                <tr>
                                                                    <td class='middle'>
                                                                        <nobr>
                                                                            {localUtil.displayName(participant)} <span class='font-weight-lighter'>- {participant.class}</span>
                                                                            {isAllowedToEditSignup ? <>
                                                                                <i
                                                                                    class='fa-solid fa-edit ml-2'
                                                                                    data-id={convention.id}
                                                                                    data-md5={participant.md5}
                                                                                    data-stage={stage.md5}
                                                                                    data-idx={idx}
                                                                                    onClick={this.editParticipant}
                                                                                />
                                                                            </> : <></>}
                                                                        </nobr>
                                                                    </td>
                                                                    {conventionResultStore.getParticipantFields(stage).map((field, index) => {
                                                                        const targetResult = participantResults.find(result => result.targetIndex === index);
                                                                        if (targetResult) {
                                                                            totalScore += targetResult.scoreTotal;
                                                                        }
                                                                        return (<>
                                                                            <td class='text-center'>
                                                                                {isAllowedToEdit ? <>
                                                                                    <input
                                                                                        type='tel'
                                                                                        pattern='[0-9]*'
                                                                                        inputmode='numeric'
                                                                                        min='0'
                                                                                        max='50'
                                                                                        delay={1500}
                                                                                        class='form-control form-control-sm py-0 px-1 text-center'
                                                                                        style='width: 40px; min-width: 40px; max-width: 40px; font-size: 1.1rem; line-height: 1.2rem;'
                                                                                        size='2'
                                                                                        data-participant={participant.id}
                                                                                        data-idx={index}
                                                                                        data-id={targetResult?.id}
                                                                                        data-stage={stage.md5}
                                                                                        data-type={stage.type}
                                                                                        name='scoreTotal'
                                                                                        onFocus={this.handleFocus}
                                                                                        // onBlur={this.handleBlur}
                                                                                        onInput={this.updateResult}
                                                                                        value={targetResult?.scoreTotal || ''}
                                                                                        disabled={isAllowToEditScore ? '' : 'disabled'}
                                                                                        autoComplete='off'
                                                                                    />
                                                                                </> : <>{targetResult?.scoreTotal}</>}
                                                                            </td>
                                                                        </>);
                                                                    })}
                                                                    <td class='text-center'>
                                                                        <span style='font-size: 1.3rem; line-height: 1.2rem; font-weight: bold;'>
                                                                            {totalScore}
                                                                        </span>
                                                                    </td>
                                                                </tr>
                                                            </>);
                                                        })}
                                                    </tbody>
                                                </table>

                                                {isConventionAdmin && !stageDone && idx >= 0 && <>
                                                    <div class='font-weight-light d-flex flex-row justify-content-between mb-1 mt-3 text-danger'>
                                                        <Text id='convention.admin-add-new-user'>Admin add new user to stage</Text>:
                                                    </div>

                                                    {isInThePast ? <>
                                                        <div class='d-flex'>
                                                            <Text id='convention.in-the-past'>Convention is in the past and closed.</Text>
                                                        </div>
                                                    </> : <>
                                                        {convention.participants && convention.participants.map(participant => {
                                                            const isYou = participant.id === user.id;
                                                            const isMissingFromThisStage = !participants?.some(p => p.id === participant.id);

                                                            if (isMissingFromThisStage) {
                                                                return (<>
                                                                    <button
                                                                        class={`btn btn-sm btn-outline-primary rounded-pill mx-1 my-1`}
                                                                        onClick={this.addParticipant}
                                                                        data-id={participant.id}
                                                                        data-md5={stage.md5}
                                                                    >
                                                                        <i class='fa-duotone fa-plus mr-1' />
                                                                        {participant.signupDate ? <>
                                                                            <span class='font-weight-lighter mr-1'>{util.isoTime(participant.signupDate)}:</span>
                                                                        </> : <></>}
                                                                        {localUtil.displayName(participant)}
                                                                        <span class='font-weight-lighter ml-1'>({participantStages[participant.id] || 0})</span>
                                                                    </button>
                                                                </>);
                                                            }
                                                            return '';
                                                        })}
                                                    </>}

                                                </>}

                                            </div>
                                        </div>
                                    </>);
                                })}
                            </> : <></>}

                            {(isAdmin && convention.id) && <>
                                <ShotTrainerFieldRemote
                                    stores={this.props.stores}
                                />
                            </>}
                            {isConventionAdmin && <>
                                <ConventionViewStageAdmin
                                    stores={this.props.stores}
                                    isInThePast={isInThePast}
                                    loadAll={this.loadAll}
                                />
                            </>}

                        </div>
                    </div>

                    {(isAdmin && convention.id) && <>
                        <ShotTrainerFieldConsumer
                            stores={this.props.stores}
                            id={convention.id}
                        />
                    </>}


                </div>
            </div>
        </>);
    }
}

export default ConventionView;
